import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Container, Header, Segment, Form, Button, Table, Message,
  Icon, Label, Modal, Progress, Divider, Grid, Statistic, Input, Tab
} from 'semantic-ui-react';
import { API } from '../../helpers/api';
import { showSuccess, showError, showWarning } from '../../helpers';
import { StatusContext } from '../../context/Status';

// 格式化字节
const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// 格式化日期
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('zh-CN');
};

// 状态标签
const StatusLabel = ({ status }) => {
  const config = {
    pending: { color: 'grey', text: '等待中', icon: 'clock' },
    estimating: { color: 'blue', text: '预估中', icon: 'calculator' },
    processing: { color: 'blue', text: '处理中', icon: 'spinner' },
    completed: { color: 'green', text: '已完成', icon: 'check' },
    failed: { color: 'red', text: '失败', icon: 'close' },
    cancelled: { color: 'grey', text: '已取消', icon: 'ban' },
  }[status] || { color: 'grey', text: status, icon: 'question' };
  
  return <Label color={config.color} size="small"><Icon name={config.icon} /> {config.text}</Label>;
};

// ==================== 导出面板 ====================
const ExportPanel = () => {
  const [statusState] = useContext(StatusContext);
  const gitEnabled = statusState?.status?.git_enabled !== false;
  
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [exportForm, setExportForm] = useState({
    export_type: 'full',
    date_from: '',
    date_to: '',
    include_repos: gitEnabled,
    compression: 'gzip',
    include_failed: true,
  });
  
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/admin/exports', { params: { page: 1, page_size: 20 } });
      if (res.data.success) {
        setTasks(res.data.data.tasks || []);
      }
    } catch (error) {
      showError('加载失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    const hasProcessing = tasks.some(t => t.status === 'processing' || t.status === 'pending');
    if (hasProcessing) {
      const interval = setInterval(loadTasks, 5000);
      return () => clearInterval(interval);
    }
  }, [tasks, loadTasks]);

  const handleEstimate = async () => {
    if (!exportForm.date_from || !exportForm.date_to) {
      showWarning('请选择日期范围');
      return;
    }
    
    setEstimating(true);
    try {
      const res = await API.post('/api/admin/exports/estimate', exportForm);
      if (res.data.success) {
        setEstimate(res.data.data);
      } else {
        showError(res.data.message);
      }
    } catch (error) {
      showError('预估失败: ' + error.message);
    } finally {
      setEstimating(false);
    }
  };

  const handleCreateExport = async () => {
    if (!exportForm.date_from || !exportForm.date_to) {
      showWarning('请选择日期范围');
      return;
    }
    
    if (estimate && !estimate.space_sufficient) {
      showError('磁盘空间不足');
      return;
    }
    
    setCreating(true);
    try {
      const res = await API.post('/api/admin/exports', exportForm);
      if (res.data.success) {
        showSuccess('导出任务已创建');
        setEstimate(null);
        loadTasks();
      } else {
        showError(res.data.message);
      }
    } catch (error) {
      showError('创建失败: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这个导出任务吗？')) return;
    
    try {
      const res = await API.delete(`/api/admin/exports/${id}`);
      if (res.data.success) {
        showSuccess('删除成功');
        loadTasks();
      } else {
        showError(res.data.message);
      }
    } catch (error) {
      showError('删除失败: ' + error.message);
    }
  };

  const copySshCommand = (task) => {
    const cmd = `rsync -avz --progress user@server:${task.server_path} ./`;
    navigator.clipboard.writeText(cmd);
    showSuccess('SSH 命令已复制');
  };

  return (
    <div>
      {/* 创建导出 */}
      <Segment>
        <Header as="h4">创建导出任务</Header>
        
        <Form>
          <Form.Group widths="equal">
            <Form.Select
              label="导出类型"
              value={exportForm.export_type}
              onChange={(e, { value }) => setExportForm({ ...exportForm, export_type: value })}
              options={[
                { key: 'full', value: 'full', text: '全部数据' },
                { key: 'executions', value: 'executions', text: '仅执行记录' },
                { key: 'queries', value: 'queries', text: '仅 LLM Query' },
              ]}
            />
            <Form.Input
              type="date"
              label="开始日期"
              value={exportForm.date_from}
              onChange={(e) => setExportForm({ ...exportForm, date_from: e.target.value })}
            />
            <Form.Input
              type="date"
              label="结束日期"
              value={exportForm.date_to}
              onChange={(e) => setExportForm({ ...exportForm, date_to: e.target.value })}
            />
          </Form.Group>
          
          <Form.Group>
            {gitEnabled && (
              <Form.Checkbox
                label="包含 Git 仓库"
                checked={exportForm.include_repos}
                onChange={(e, { checked }) => setExportForm({ ...exportForm, include_repos: checked })}
              />
            )}
            <Form.Checkbox
              label="包含失败请求"
              checked={exportForm.include_failed}
              onChange={(e, { checked }) => setExportForm({ ...exportForm, include_failed: checked })}
            />
          </Form.Group>
          
          <Button onClick={handleEstimate} loading={estimating} disabled={estimating}>
            <Icon name="calculator" /> 预估空间
          </Button>
        </Form>

        {estimate && (
          <Segment secondary style={{ marginTop: 16 }}>
            <Grid columns={gitEnabled ? 4 : 3} divided>
              <Grid.Row>
                <Grid.Column>
                  <Statistic size="mini">
                    <Statistic.Value>{estimate.execution_count || 0}</Statistic.Value>
                    <Statistic.Label>执行记录</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column>
                  <Statistic size="mini">
                    <Statistic.Value>{estimate.query_count || 0}</Statistic.Value>
                    <Statistic.Label>LLM Query</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                {gitEnabled && (
                  <Grid.Column>
                    <Statistic size="mini">
                      <Statistic.Value>{estimate.repo_count || 0}</Statistic.Value>
                      <Statistic.Label>Git 仓库</Statistic.Label>
                    </Statistic>
                  </Grid.Column>
                )}
                <Grid.Column>
                  <Statistic size="mini" color={estimate.space_sufficient ? 'green' : 'red'}>
                    <Statistic.Value>{estimate.total_size_human}</Statistic.Value>
                    <Statistic.Label>预估大小</Statistic.Label>
                  </Statistic>
                </Grid.Column>
              </Grid.Row>
            </Grid>
            
            <Divider />
            
            <Message info size="small">
              <Icon name="info circle" />
              <strong>估算说明：</strong>
              Query 约 5KB/条，执行记录约 2KB/条{gitEnabled && '，Git 仓库约 50MB/个（裸仓库平均值）'}
            </Message>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
              <span>
                可用空间: {formatBytes(estimate.available_space)}
                {estimate.space_sufficient ? (
                  <Label color="green" size="small" style={{ marginLeft: 8 }}>✓ 充足</Label>
                ) : (
                  <Label color="red" size="small" style={{ marginLeft: 8 }}>✗ 不足</Label>
                )}
              </span>
              
              <Button 
                primary 
                onClick={handleCreateExport} 
                loading={creating}
                disabled={!estimate.space_sufficient || creating}
              >
                <Icon name="download" /> 确认导出
              </Button>
            </div>
          </Segment>
        )}
      </Segment>

      {/* 任务列表 */}
      <Segment loading={loading}>
        <Header as="h4">导出任务</Header>
        
        <Table compact celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>ID</Table.HeaderCell>
              <Table.HeaderCell>日期范围</Table.HeaderCell>
              <Table.HeaderCell>状态</Table.HeaderCell>
              <Table.HeaderCell>大小</Table.HeaderCell>
              <Table.HeaderCell>创建时间</Table.HeaderCell>
              <Table.HeaderCell>操作</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          
          <Table.Body>
            {tasks.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan="6" textAlign="center">暂无导出任务</Table.Cell>
              </Table.Row>
            ) : (
              tasks.map(task => (
                <Table.Row key={task.id}>
                  <Table.Cell><code style={{ fontSize: 11 }}>{task.id.substring(0, 16)}...</code></Table.Cell>
                  <Table.Cell>{task.date_from?.substring(0, 10)} ~ {task.date_to?.substring(0, 10)}</Table.Cell>
                  <Table.Cell>
                    <StatusLabel status={task.status} />
                    {task.status === 'processing' && <Progress percent={task.progress} size="tiny" indicating style={{ marginTop: 4 }} />}
                  </Table.Cell>
                  <Table.Cell>{formatBytes(task.actual_size || task.estimated_total)}</Table.Cell>
                  <Table.Cell>{formatDate(task.created_at)}</Table.Cell>
                  <Table.Cell>
                    <Button.Group size="mini">
                      <Button icon onClick={() => { setSelectedTask(task); setShowDetail(true); }}><Icon name="eye" /></Button>
                      {task.status === 'completed' && (
                        <Button icon color="blue" onClick={() => copySshCommand(task)} title="复制 SSH 命令"><Icon name="terminal" /></Button>
                      )}
                      <Button icon color="red" onClick={() => handleDelete(task.id)}><Icon name="trash" /></Button>
                    </Button.Group>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table>
      </Segment>

      {/* 详情弹窗 */}
      <Modal open={showDetail} onClose={() => setShowDetail(false)} size="small">
        <Modal.Header>导出详情</Modal.Header>
        <Modal.Content>
          {selectedTask && (
            <div>
              <p><strong>ID:</strong> {selectedTask.id}</p>
              <p><strong>状态:</strong> <StatusLabel status={selectedTask.status} /> {selectedTask.current_phase}</p>
              <p><strong>数据量:</strong> {selectedTask.execution_count || 0} 执行 / {selectedTask.query_count || 0} Query{gitEnabled && ` / ${selectedTask.repo_count || 0} 仓库`}</p>
              <p><strong>大小:</strong> {formatBytes(selectedTask.actual_size)}</p>
              
              {selectedTask.status === 'completed' && (
                <>
                  <Divider />
                  <p><strong>服务器路径:</strong></p>
                  <code style={{ display: 'block', padding: 8, backgroundColor: '#1e1e1e', color: '#d4d4d4', borderRadius: 4, fontSize: 12 }}>
                    {selectedTask.server_path}
                  </code>
                  <p style={{ marginTop: 12 }}><strong>SSH 命令:</strong></p>
                  <code style={{ display: 'block', padding: 8, backgroundColor: '#1e1e1e', color: '#d4d4d4', borderRadius: 4, fontSize: 12 }}>
                    rsync -avz --progress user@server:{selectedTask.server_path} ./
                  </code>
                  <Button size="small" style={{ marginTop: 8 }} onClick={() => copySshCommand(selectedTask)}>
                    <Icon name="copy" /> 复制命令
                  </Button>
                </>
              )}
              
              {selectedTask.error_message && (
                <Message error size="small">{selectedTask.error_message}</Message>
              )}
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowDetail(false)}>关闭</Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

// ==================== 清空面板 ====================
const CleanupPanel = () => {
  const [statusState] = useContext(StatusContext);
  const gitEnabled = statusState?.status?.git_enabled !== false;
  
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [cleanupForm, setCleanupForm] = useState({
    cleanup_type: 'queries',
    date_from: '',
    date_to: '',
    reason: '',
    backup_before: true,
  });
  
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmCode, setConfirmCode] = useState('');
  const [userInputCode, setUserInputCode] = useState('');
  const [cleanupTaskId, setCleanupTaskId] = useState('');
  const [confirmExpires, setConfirmExpires] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/admin/cleanup/history');
      if (res.data.success) {
        setHistory(res.data.data.tasks || []);
      }
    } catch (error) {
      showError('加载失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (confirmExpires) {
      const timer = setInterval(() => {
        const remaining = Math.max(0, Math.floor((new Date(confirmExpires) - new Date()) / 1000));
        setCountdown(remaining);
        if (remaining <= 0) {
          setConfirmModal(false);
          showWarning('确认码已过期');
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [confirmExpires]);

  const handlePreview = async () => {
    if (!cleanupForm.date_from || !cleanupForm.date_to) {
      showWarning('请选择日期范围');
      return;
    }
    
    setPreviewing(true);
    try {
      const res = await API.post('/api/admin/cleanup/preview', cleanupForm);
      if (res.data.success) {
        setPreview(res.data.data);
      } else {
        showError(res.data.message);
      }
    } catch (error) {
      showError('预览失败: ' + error.message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleInitiate = async () => {
    if (!cleanupForm.reason.trim()) {
      showWarning('请填写清空原因');
      return;
    }
    
    try {
      const res = await API.post('/api/admin/cleanup/initiate', cleanupForm);
      if (res.data.success) {
        setConfirmCode(res.data.data.confirm_code);
        setCleanupTaskId(res.data.data.id);
        setConfirmExpires(res.data.data.confirm_expires);
        setUserInputCode('');
        setConfirmModal(true);
      } else {
        showError(res.data.message);
      }
    } catch (error) {
      showError('发起失败: ' + error.message);
    }
  };

  const handleConfirm = async () => {
    if (userInputCode !== confirmCode) {
      showError('确认码不正确');
      return;
    }
    
    setConfirming(true);
    try {
      const res = await API.post('/api/admin/cleanup/confirm', {
        id: cleanupTaskId,
        confirm_code: userInputCode,
      });
      
      if (res.data.success) {
        showSuccess(`清空完成！删除了 ${res.data.data.deleted_queries} 条 Query 和 ${res.data.data.deleted_executions} 条执行记录`);
        setConfirmModal(false);
        setPreview(null);
        loadHistory();
      } else {
        showError(res.data.message);
      }
    } catch (error) {
      showError('清空失败: ' + error.message);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div>
      <Message warning size="small">
        <Icon name="warning sign" />
        数据清空是<strong>不可逆操作</strong>，建议启用删除前备份。
      </Message>

      {/* 清空配置 */}
      <Segment>
        <Header as="h4">清空配置</Header>
        
        <Form>
          <Form.Group widths="equal">
            <Form.Select
              label="清空类型"
              value={cleanupForm.cleanup_type}
              onChange={(e, { value }) => setCleanupForm({ ...cleanupForm, cleanup_type: value })}
              options={[
                { key: 'queries', value: 'queries', text: '仅 LLM Query' },
                { key: 'executions', value: 'executions', text: '仅执行记录' },
                { key: 'all', value: 'all', text: '全部数据' },
              ]}
            />
            <Form.Input
              type="date"
              label="开始日期"
              value={cleanupForm.date_from}
              onChange={(e) => setCleanupForm({ ...cleanupForm, date_from: e.target.value })}
            />
            <Form.Input
              type="date"
              label="结束日期"
              value={cleanupForm.date_to}
              onChange={(e) => setCleanupForm({ ...cleanupForm, date_to: e.target.value })}
            />
          </Form.Group>
          
          <Form.TextArea
            label="清空原因（必填）"
            placeholder="请说明清空原因..."
            value={cleanupForm.reason}
            onChange={(e) => setCleanupForm({ ...cleanupForm, reason: e.target.value })}
            rows={2}
          />
          
          <Form.Checkbox
            label="删除前自动备份"
            checked={cleanupForm.backup_before}
            onChange={(e, { checked }) => setCleanupForm({ ...cleanupForm, backup_before: checked })}
          />
          
          <Button onClick={handlePreview} loading={previewing} disabled={previewing}>
            <Icon name="eye" /> 预览
          </Button>
        </Form>

        {preview && (
          <Segment secondary style={{ marginTop: 16 }}>
            <Grid columns={3} divided>
              <Grid.Row>
                <Grid.Column>
                  <Statistic size="mini" color="red">
                    <Statistic.Value>{preview.query_count || 0}</Statistic.Value>
                    <Statistic.Label>Query 将删除</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column>
                  <Statistic size="mini" color="red">
                    <Statistic.Value>{preview.execution_count || 0}</Statistic.Value>
                    <Statistic.Label>执行将删除</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column>
                  <Statistic size="mini" color="green">
                    <Statistic.Value>{preview.space_human || '0 B'}</Statistic.Value>
                    <Statistic.Label>预计释放</Statistic.Label>
                  </Statistic>
                </Grid.Column>
              </Grid.Row>
            </Grid>
            
            <Divider />
            
            <div style={{ textAlign: 'right' }}>
              <Button color="red" onClick={handleInitiate} disabled={!cleanupForm.reason.trim()}>
                <Icon name="trash" /> 发起清空
              </Button>
            </div>
          </Segment>
        )}
      </Segment>

      {/* 历史记录 */}
      <Segment loading={loading}>
        <Header as="h4">清空历史</Header>
        
        <Table compact celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>时间</Table.HeaderCell>
              <Table.HeaderCell>类型</Table.HeaderCell>
              <Table.HeaderCell>删除数量</Table.HeaderCell>
              <Table.HeaderCell>释放空间</Table.HeaderCell>
              <Table.HeaderCell>原因</Table.HeaderCell>
              <Table.HeaderCell>备份</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          
          <Table.Body>
            {history.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan="6" textAlign="center">暂无记录</Table.Cell>
              </Table.Row>
            ) : (
              history.map(task => (
                <Table.Row key={task.id}>
                  <Table.Cell>{formatDate(task.completed_at)}</Table.Cell>
                  <Table.Cell>{task.cleanup_type}</Table.Cell>
                  <Table.Cell>{task.deleted_queries || 0} Q / {task.deleted_execs || 0} E</Table.Cell>
                  <Table.Cell>{formatBytes(task.freed_space_bytes)}</Table.Cell>
                  <Table.Cell style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {task.reason}
                  </Table.Cell>
                  <Table.Cell>
                    {task.backup_export_id ? <Label color="green" size="tiny">有</Label> : <Label size="tiny">无</Label>}
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table>
      </Segment>

      {/* 确认弹窗 */}
      <Modal open={confirmModal} onClose={() => setConfirmModal(false)} size="tiny">
        <Modal.Header><Icon name="lock" color="red" /> 确认清空</Modal.Header>
        <Modal.Content>
          <Message error size="small">
            即将删除 <strong>{preview?.query_count || 0}</strong> Query + <strong>{preview?.execution_count || 0}</strong> 执行记录
          </Message>
          
          <div style={{ textAlign: 'center', margin: '20px 0' }}>
            <p>请输入确认码：</p>
            <div style={{ fontSize: 28, fontFamily: 'monospace', color: '#d32f2f', fontWeight: 'bold', margin: '12px 0', letterSpacing: 6 }}>
              {confirmCode}
            </div>
            
            <Input
              placeholder="输入确认码"
              value={userInputCode}
              onChange={(e) => setUserInputCode(e.target.value.toUpperCase())}
              style={{ fontFamily: 'monospace', letterSpacing: 4, width: 150 }}
            />
            
            <p style={{ marginTop: 12, color: '#888', fontSize: 12 }}>
              <Icon name="clock" /> {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')} 后过期
            </p>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setConfirmModal(false)}>取消</Button>
          <Button negative onClick={handleConfirm} disabled={userInputCode !== confirmCode || confirming} loading={confirming}>
            确认删除
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

// ==================== 主页面 ====================
const DataManagementPage = () => {
  const [config, setConfig] = useState({ export_base_path: '', expire_days: 30, max_concurrent: 2 });
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await API.get('/api/admin/exports/config');
        if (res.data.success) {
          setConfig(res.data.data);
        }
      } catch (e) {
        // ignore
      }
    };
    loadConfig();
  }, []);

  const handleSaveConfig = async () => {
    if (!config.export_base_path) {
      showWarning('请填写导出路径');
      return;
    }
    setConfigSaving(true);
    try {
      const res = await API.post('/api/admin/exports/config', config);
      if (res.data.success) {
        showSuccess('配置已保存');
      } else {
        showError(res.data.message);
      }
    } catch (e) {
      showError('保存失败: ' + e.message);
    } finally {
      setConfigSaving(false);
    }
  };

  const ConfigPanel = () => (
    <Segment>
      <Header as="h4">导出配置</Header>
      <Form>
        <Form.Input
          label="导出根目录"
          placeholder="/data/exports"
          value={config.export_base_path}
          onChange={(e) => setConfig({ ...config, export_base_path: e.target.value })}
        />
        <Form.Group widths="equal">
          <Form.Input
            type="number"
            label="文件过期天数"
            value={config.expire_days}
            onChange={(e) => setConfig({ ...config, expire_days: parseInt(e.target.value) || 30 })}
          />
          <Form.Input
            type="number"
            label="最大并发导出数"
            value={config.max_concurrent}
            onChange={(e) => setConfig({ ...config, max_concurrent: parseInt(e.target.value) || 2 })}
          />
        </Form.Group>
        <Button primary onClick={handleSaveConfig} loading={configSaving}>
          <Icon name="save" /> 保存配置
        </Button>
      </Form>
      
      <Message info size="small" style={{ marginTop: 16 }}>
        <Icon name="info circle" />
        导出文件将保存到服务器的指定路径，管理员可通过 SSH 拉取。
      </Message>
    </Segment>
  );

  const panes = [
    {
      menuItem: { key: 'export', icon: 'download', content: '数据导出' },
      render: () => <Tab.Pane><ExportPanel /></Tab.Pane>,
    },
    {
      menuItem: { key: 'cleanup', icon: 'trash alternate', content: '数据清空' },
      render: () => <Tab.Pane><CleanupPanel /></Tab.Pane>,
    },
    {
      menuItem: { key: 'config', icon: 'setting', content: '配置' },
      render: () => <Tab.Pane><ConfigPanel /></Tab.Pane>,
    },
  ];

  return (
    <Container>
      <Header as="h2">
        <Icon name="database" />
        <Header.Content>
          数据管理
          <Header.Subheader>导出训练数据 / 清理过期数据</Header.Subheader>
        </Header.Content>
      </Header>

      <Tab panes={panes} />
    </Container>
  );
};

export default DataManagementPage;

